// Central readers for the per-project view of pipeline / scoring config.
//
// The backend stores a per-project override and exposes the resolved view
// as `effective_*` on the Project payload. Anywhere in the app that needs
// to display or filter on these values should go through these helpers,
// otherwise the UI keeps showing the project_type's static defaults even
// after the user edited them in Settings.

import type {
  PipelineStatus,
  Project,
  ScoringConfig,
  ScoringRule,
} from "@/api/types";

const DEFAULT_SCORING_THRESHOLD = 55;
/** Mirrors the backend's `NO_ANSWER_ROUTE_THRESHOLD`. Keep the two in
 *  step: the badge reads "N/max", so a front-side default higher than
 *  the backend's makes the counter jump to its routed status before the
 *  displayed maximum is reached. */
const DEFAULT_MAX_NO_ANSWER_CALLS = 5;
/** Mirrors the backend's `NO_ANSWER_COOLDOWN_HOURS`. */
const DEFAULT_NO_ANSWER_COOLDOWN_HOURS = 24;

/** Pipeline status a lead is parked in when it hit the no-answer
 *  threshold with no reachable decision-maker email. Parked, NOT lost:
 *  the status ships `is_lost: false`, stays visible in the listings, and
 *  a PATCH back to `to_be_contacted` re-queues it (the backend zeroes
 *  the counter on that transition). */
export const STATUS_UNREACHABLE = "unreachable";

/** Stable id for the M&A project type. Centralised here so the
 *  CompanyDetail panel and the listing table cells gate the same
 *  way on "is this a Pappers-enriched M&A dossier?" without each
 *  surface duplicating the literal. */
export const MA_PROJECT_TYPE_ID = "m_and_a";

/** True when the project is the M&A flavour. Both id paths are
 *  checked because some endpoints (today's ProjectRead, future
 *  partial /me responses) ship one or the other. */
export function isMAProject(project: Project | null | undefined): boolean {
  if (!project) return false;
  return (
    project.project_type_id === MA_PROJECT_TYPE_ID ||
    project.project_type?.id === MA_PROJECT_TYPE_ID
  );
}

export function getEffectivePipelineStatuses(
  project: Project | null | undefined,
): PipelineStatus[] {
  if (!project) return [];
  if (Array.isArray(project.effective_pipeline_statuses)) {
    return project.effective_pipeline_statuses;
  }
  const fromConfig = project.config?.pipeline_statuses;
  if (Array.isArray(fromConfig)) return fromConfig as PipelineStatus[];
  return (project.project_type.pipeline_statuses ?? []) as PipelineStatus[];
}

export function getEffectiveScoringRules(
  project: Project | null | undefined,
): ScoringRule[] {
  if (project?.cfo_effective_scoring) return project.cfo_effective_scoring.scoring_rules as unknown as ScoringRule[];
  if (!project) return [];
  if (Array.isArray(project.effective_scoring_rules)) {
    return project.effective_scoring_rules as unknown as ScoringRule[];
  }
  const fromConfig = project.config?.scoring_rules;
  if (Array.isArray(fromConfig)) return fromConfig as unknown as ScoringRule[];
  return (project.project_type.scoring_rules ?? []) as unknown as ScoringRule[];
}

export function getEffectiveScoringThreshold(
  project: Project | null | undefined,
): number {
  if (project?.cfo_effective_scoring) return project.cfo_effective_scoring.scoring_threshold;
  if (!project) return DEFAULT_SCORING_THRESHOLD;
  if (typeof project.effective_scoring_threshold === "number") {
    return project.effective_scoring_threshold;
  }
  const fromConfig = project.config?.scoring_threshold;
  if (typeof fromConfig === "number") return fromConfig;
  return DEFAULT_SCORING_THRESHOLD;
}

/** Unanswered calls before the backend routes a lead out of the call
 *  queue. At the threshold it goes to `email_pending` when a decision-
 *  maker has an email, otherwise to `unreachable` — it is NOT refused,
 *  which is what the counter used to claim. Surfaced in the Call Center
 *  badge (X/N) and the last-attempt warning. Stored under
 *  `project.config.max_no_answer_calls`. */
export function getMaxNoAnswerCalls(
  project: Project | null | undefined,
): number {
  if (!project) return DEFAULT_MAX_NO_ANSWER_CALLS;
  const fromConfig = project.config?.max_no_answer_calls;
  if (typeof fromConfig === "number" && Number.isFinite(fromConfig) && fromConfig > 0) {
    return Math.floor(fromConfig);
  }
  return DEFAULT_MAX_NO_ANSWER_CALLS;
}

/** Hours a lead stays out of the `no_answer_retry` bucket after an
 *  unanswered call. The backend applies this to the bucket counts, the
 *  filtered listing and the session picker alike, so a lead called this
 *  morning legitimately disappears from the queue until tomorrow — the
 *  UI has to say so, otherwise it reads as a bug. Stored under
 *  `project.config.no_answer_cooldown_hours`. */
export function getNoAnswerCooldownHours(
  project: Project | null | undefined,
): number {
  const fromConfig = project?.config?.no_answer_cooldown_hours;
  if (
    typeof fromConfig === "number" &&
    Number.isFinite(fromConfig) &&
    fromConfig > 0
  ) {
    return Math.floor(fromConfig);
  }
  return DEFAULT_NO_ANSWER_COOLDOWN_HOURS;
}

export function getEffectiveScoringExclusions(
  project: Project | null | undefined,
): Array<Record<string, unknown>> {
  if (project?.cfo_effective_scoring) return project.cfo_effective_scoring.scoring_exclusions;
  if (!project) return [];
  if (Array.isArray(project.effective_scoring_exclusions)) {
    return project.effective_scoring_exclusions;
  }
  const fromConfig = project.config?.scoring_exclusions;
  if (Array.isArray(fromConfig)) {
    return fromConfig as Array<Record<string, unknown>>;
  }
  return [];
}

/** CFO v2 dynamic scoring config (dimension breakdowns + keyword lists
 *  + booster / stage-duration scalars). Resolution order mirrors the
 *  other `getEffective*` readers, with two extra fallbacks because the
 *  backend may not ship `effective_scoring_config` yet:
 *    1. `effective_scoring_config` (server-merged view),
 *    2. the per-project override `config.scoring_config`,
 *    3. the project-type default `project_type.features.scoring_config`.
 *  Returns `{}` when none is present (older project types / M&A). The
 *  raw `config` / `features` objects are untyped on the wire, so we
 *  read them through guarded casts. */
export function getEffectiveScoringConfig(
  project: Project | null | undefined,
): ScoringConfig {
  if (project?.cfo_effective_scoring) return project.cfo_effective_scoring.scoring_config as ScoringConfig;
  if (!project) return {};
  if (isPlainObject(project.effective_scoring_config)) {
    return project.effective_scoring_config as ScoringConfig;
  }
  const fromConfig = (project.config as Record<string, unknown> | undefined)
    ?.scoring_config;
  if (isPlainObject(fromConfig)) return fromConfig as ScoringConfig;
  // ProjectType.features is typed as `string[]` in the client but the
  // backend ships it as an object dict (it carries scoring_config among
  // other engine config). Read it through `unknown` until the type is
  // corrected.
  const features = project.project_type?.features as unknown as
    | Record<string, unknown>
    | undefined;
  const fromType = features?.scoring_config;
  if (isPlainObject(fromType)) return fromType as ScoringConfig;
  return {};
}

function isPlainObject(v: unknown): v is Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v);
}

/**
 * Resolve a status's display label from its key. Used by activity rows,
 * dropdowns, and filters that only carry the key around.
 */
export function getStatusLabel(
  statusKey: string,
  statuses: readonly PipelineStatus[],
): string {
  const status = statuses.find((s) => s.key === statusKey);
  return status?.label ?? statusKey.replace(/_/g, " ");
}

/**
 * Resolve the color name (e.g. "indigo") declared on a pipeline status.
 * Returns null when the status isn't found or hasn't been given a color —
 * callers can fall back to a static default in that case.
 */
export function getStatusColorName(
  statusKey: string,
  statuses: readonly PipelineStatus[],
): string | null {
  const status = statuses.find((s) => s.key === statusKey);
  return status?.color ?? null;
}
